import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import { showToast } from "../../../core/utils/alert";
import CommonSelect, { Option } from "../../../core/common/commonSelect";

interface Props {
  onSuccess: () => void;
  onClose: () => void;
  handleCreate: (formData: FormData) => Promise<any>;
  isCreating: boolean;
}

const AddOfflinePaymentModal: React.FC<Props> = ({
  onSuccess,
  onClose,
  handleCreate,
  isCreating,
}) => {
  const userRole = useSelector(selectUserRole);
  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList();
  const centers = centerResponse?.data?.items || [];

  const centerOptions = centers.map((c) => ({
    value: c.id.toString(),
    label: c.name,
  }));

  const paymentMethodOptions = [
    { value: "cash", label: "Cash" },
    { value: "mpay", label: "M-Pay" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "upi", label: "UPI" },
  ];

  const [form, setForm] = useState({
    student_id: "",
    center_id: "",
    amount: "",
    payment_date: "",
    payment_method: "",
    reference_number: "",
    remarks: "",
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setReceiptFile(file);
    setErrors((prev) => ({ ...prev, receipt: "" }));
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setReceiptPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(null);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.student_id) newErrors.student_id = "Student ID is required";
    if (!form.center_id) newErrors.center_id = "Center is required";
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
      newErrors.amount = "Valid amount is required";
    if (!form.payment_date) newErrors.payment_date = "Payment date is required";
    if (!receiptFile) newErrors.receipt = "Receipt file is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const fd = new FormData();
    fd.append("student_id", form.student_id);
    fd.append("center_id", form.center_id);
    fd.append("amount", form.amount);
    fd.append("payment_date", form.payment_date);
    if (form.payment_method) fd.append("payment_method", form.payment_method);
    if (form.reference_number) fd.append("reference_number", form.reference_number);
    if (form.remarks) fd.append("remarks", form.remarks);
    fd.append("status", "approved");
    if (receiptFile) fd.append("receipt", receiptFile);

    try {
      const res = await handleCreate(fd);
      if (res?.success) {
        showToast({ text: "Payment created successfully", icon: "success" });
        onSuccess();
        onClose();
      } else {
        showToast({ text: res?.message || "Failed to create payment", icon: "error" });
      }
    } catch (err: any) {
      showToast({
        text: err?.data?.message || "An error occurred",
        icon: "error",
      });
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        style={{ zIndex: 1050 }}
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content shadow-lg border-0" style={{ borderRadius: "16px", overflow: "hidden" }}>
            {/* Header */}
            <div
              className="modal-header border-0 px-4 pt-4 pb-3"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              <div className="d-flex align-items-center">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(255,255,255,0.25)",
                  }}
                >
                  <i className="ti ti-cash fs-20 text-white" />
                </div>
                <div>
                  <h5 className="modal-title text-white mb-0 fw-semibold">
                    Record Offline Payment
                  </h5>
                  <small className="text-white opacity-75">
                    Add a new offline payment entry
                  </small>
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white ms-auto"
                onClick={onClose}
              />
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit}>
              <div className="modal-body px-4 py-4">
                <div className="row g-3">
                  {/* Student ID */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Student ID <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${errors.student_id ? "is-invalid" : ""}`}
                      placeholder="Enter student ID"
                      value={form.student_id}
                      onChange={(e) => handleChange("student_id", e.target.value)}
                    />
                    {errors.student_id && (
                      <div className="invalid-feedback">{errors.student_id}</div>
                    )}
                  </div>

                  {/* Center */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Center <span className="text-danger">*</span>
                    </label>
                    <CommonSelect
                      className={`select ${errors.center_id ? "is-invalid" : ""}`}
                      options={centerOptions}
                      value={
                        centerOptions.find((o) => o.value === form.center_id) || null
                      }
                      onChange={(opt: Option | null) =>
                        handleChange("center_id", opt?.value || "")
                      }
                    />
                    {errors.center_id && (
                      <div className="text-danger small mt-1">{errors.center_id}</div>
                    )}
                  </div>

                  {/* Amount */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Amount <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                        placeholder="0.00"
                        value={form.amount}
                        onChange={(e) => handleChange("amount", e.target.value)}
                      />
                      {errors.amount && (
                        <div className="invalid-feedback">{errors.amount}</div>
                      )}
                    </div>
                  </div>

                  {/* Payment Date */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Payment Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${errors.payment_date ? "is-invalid" : ""}`}
                      value={form.payment_date}
                      onChange={(e) => handleChange("payment_date", e.target.value)}
                    />
                    {errors.payment_date && (
                      <div className="invalid-feedback">{errors.payment_date}</div>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Payment Method</label>
                    <CommonSelect
                      className="select"
                      options={paymentMethodOptions}
                      value={
                        paymentMethodOptions.find(
                          (o) => o.value === form.payment_method
                        ) || null
                      }
                      onChange={(opt: Option | null) =>
                        handleChange("payment_method", opt?.value || "")
                      }
                    />
                  </div>

                  {/* Reference Number */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Reference Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Transaction / Ref. No."
                      value={form.reference_number}
                      onChange={(e) => handleChange("reference_number", e.target.value)}
                    />
                  </div>

                  {/* Remarks */}
                  <div className="col-12">
                    <label className="form-label fw-medium">Remarks</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Any notes or remarks about this payment..."
                      value={form.remarks}
                      onChange={(e) => handleChange("remarks", e.target.value)}
                    />
                  </div>

                  {/* Receipt Upload */}
                  <div className="col-12">
                    <label className="form-label fw-medium">
                      Receipt <span className="text-danger">*</span>
                      <small className="text-muted ms-1">(jpg, png, pdf)</small>
                    </label>
                    <div
                      className={`border rounded-3 p-3 text-center ${
                        errors.receipt ? "border-danger" : "border-dashed"
                      }`}
                      style={{
                        cursor: "pointer",
                        background: receiptFile ? "#f8f9ff" : "#fafafa",
                        borderStyle: "dashed",
                        minHeight: 100,
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {receiptPreview ? (
                        <img
                          src={receiptPreview}
                          alt="Receipt preview"
                          style={{ maxHeight: 120, borderRadius: 8 }}
                        />
                      ) : receiptFile ? (
                        <div className="d-flex align-items-center justify-content-center gap-2 py-3">
                          <i className="ti ti-file-description fs-24 text-primary" />
                          <span className="text-muted small">{receiptFile.name}</span>
                        </div>
                      ) : (
                        <div className="py-3">
                          <i className="ti ti-cloud-upload fs-28 text-muted mb-1" />
                          <p className="text-muted small mb-0">
                            Click to upload receipt
                          </p>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,application/pdf"
                      ref={fileInputRef}
                      className="d-none"
                      onChange={handleFileChange}
                    />
                    {errors.receipt && (
                      <div className="text-danger small mt-1">{errors.receipt}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer border-0 px-4 pb-4 pt-2">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={onClose}
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4 d-flex align-items-center gap-2"
                  disabled={isCreating}
                  style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none",
                  }}
                >
                  {isCreating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-check" />
                      Save Payment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddOfflinePaymentModal;
